import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./TuckPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Tuck" (/tuck) — a NON-ONBOARDING, in-app upgrade for an active free user,
// built to the standing directive: SECURITY & PRIVACY.
//
// The mechanic is the one gesture no other paywall here uses: you ZIP a soft
// private POUCH shut. Your memories rest in the open mouth of the pouch; you
// drag a lock-shaped pull along the seam and it fuses left→right, the photos
// tuck safely down inside, and the four REAL protections emboss and light up
// TOGETHER on the sealed face — Only-you unlock · Encrypted backup · Break-in
// alerts · Private sync.
//
// Deliberately avoids every taken mechanic (wax seal, slider, plug/roller,
// power switch, deadbolt, dial, biometric pad, ECG, pattern-trace, device
// re-pair handshake, tempered-glass panes). The control IS the hero. Register:
// calm, tidy finality — the quiet satisfaction of putting your valuables away —
// not fear, not countdowns. Value framing is CONTRAST + tactile completion:
// "put the full protection around them, then seal it." Protections light
// together, never as a checklist.
//
// Honest & App-Store compliant: the featured, most-prominent price is the YEARLY
// plan (7-day free trial, per-month broken out, honest Save %); monthly is a
// small secondary line. No scarcity, no countdown, no data-loss claim — sealing
// only ADDS protection; nothing is ever deleted or locked away from you. Reuses
// only the house font, colour tokens and the bundled byheart memory photos.
// Renders standalone on debug data; no native injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The memories resting in the mouth of the pouch (front → back).
const MEMORIES = ["m04", "m10", "m01", "m08", "m14"].map(photo)

type Guard = { label: string; sub: string; icon: JSX.Element }

// The four REAL Premium protections. They arm TOGETHER the instant the pouch
// seals — presented as embossed seals on the pouch face, not a bullet list.
const GUARDS: Guard[] = [
  {
    label: "Only-you unlock",
    sub: "Face ID / PIN",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
        <path
          d="M7 10V7.4a5 5 0 0 1 10 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="4.8"
          y="10"
          width="14.4"
          height="10"
          rx="2.6"
          fill="currentColor"
        />
        <circle cx="12" cy="14.6" r="1.5" fill="var(--tk-hole)" />
      </svg>
    ),
  },
  {
    label: "Encrypted backup",
    sub: "End-to-end",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
        <path
          d="M12 3.4l7 3v5c0 4.6-3 7.6-7 9-4-1.4-7-4.4-7-9v-5l7-3z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M8.7 12.2l2.2 2.2 4.4-4.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Break-in alerts",
    sub: "Catches intruders",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
        <path
          d="M2.5 12S6 5.6 12 5.6 21.5 12 21.5 12 18 18.4 12 18.4 2.5 12 2.5 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="2.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    ),
  },
  {
    label: "Private sync",
    sub: "Across your devices",
    icon: (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
        <path
          d="M5 12a7 7 0 0 1 11.9-5M19 12A7 7 0 0 1 7.1 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M16.4 3.6V7h-3.4M7.6 20.4V17h3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
]

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)

function TuckPaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const darkMode = state.initialData?.darkMode ?? false
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // Seal progress 0 (open) → 1 (sealed). Drives the whole instrument.
  const [progress, setProgress] = useState(0)
  const [dragging, setDragging] = useState(false)
  const sealed = progress > 0.985

  const trackRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)
  const sealedOnce = useRef(false)

  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#0c0e16" : "#e7ebf6")
    actions.setFooterInsetColor(darkMode ? "#0a0c13" : "#e0e5f3")
  }, [actions, darkMode])

  useEffect(() => {
    if (sealed && !sealedOnce.current) {
      sealedOnce.current = true
      native.logMixpanelEvent("html_tuck_sealed")
    }
  }, [sealed])

  const progressFromClientX = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    return clamp01((clientX - rect.left) / rect.width)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      draggingRef.current = true
      setDragging(true)
      ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
      setProgress(progressFromClientX(e.clientX))
    },
    [progressFromClientX],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      setProgress(progressFromClientX(e.clientX))
    },
    [progressFromClientX],
  )

  const settle = useCallback(() => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    // Snap: cross the two-thirds mark and it seals; otherwise fall back open.
    setProgress((p) => (p > 0.62 ? 1 : 0))
  }, [])

  // Tap / keyboard fallback (also the reduced-motion path): toggle sealed.
  const toggleSeal = useCallback(() => {
    setProgress((p) => (p > 0.5 ? 0 : 1))
  }, [])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    native.logMixpanelEvent("html_tuck_purchase", { plan })
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  // Derived motion values.
  const closedPct = `${(progress * 100).toFixed(2)}%`
  const pullLeft = `${(progress * 100).toFixed(2)}%`
  // Photos tuck down and fade as the mouth closes.
  const tuck = progress
  const noTransition = dragging

  const ctaLabel =
    plan === "yearly"
      ? yearly?.hasTrial
        ? "Start 7-day free trial"
        : "Protect my memories"
      : "Protect my memories"

  const ctaSub =
    plan === "yearly"
      ? yearly?.hasTrial
        ? `${yearlyPrice}/year after your 7-day free trial — just ${perMonth}/mo. Cancel anytime.`
        : `${yearlyPrice}/year — just ${perMonth}/mo. Cancel anytime.`
      : `${monthlyPrice}/month. Cancel anytime.`

  return (
    <div
      className={`${styles.root} ${darkMode ? styles.isDark : ""} ${
        sealed ? styles.sealed : ""
      }`}
    >
      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 10, paddingBottom: bottomInset + 14 }}
      >
        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
              <path
                d="M8 10V7.6a4 4 0 0 1 8 0V10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
              <rect
                x="5.4"
                y="10"
                width="13.2"
                height="9.4"
                rx="2.6"
                fill="currentColor"
              />
              <circle cx="12" cy="14.2" r="1.35" fill="var(--tk-brand-hole)" />
            </svg>
            <span>
              Private Photo Vault <b>Premium</b>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={handleClose}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ── Hero copy ───────────────────────────────────────────────────── */}
        <header className={styles.hero}>
          <span className={styles.kicker}>Your private pouch</span>
          <h1 className={styles.title}>
            Tuck every memory
            <br />
            safely away.
          </h1>
          <p className={styles.sub}>
            Wrap encrypted backup, private sync and break-in alerts around your
            photos — then seal it shut. Only you hold the key.
          </p>
        </header>

        {/* ── The pouch (hero instrument) ─────────────────────────────────── */}
        <div className={styles.stage}>
          <div
            className={styles.pouch}
            style={{ ["--p" as any]: progress }}
            data-sealed={sealed}
          >
            {/* Memories resting in the open mouth, tucking down as it seals */}
            <div className={styles.mouth} aria-hidden>
              <div className={styles.memories}>
                {MEMORIES.map((src, i) => {
                  const mid = (MEMORIES.length - 1) / 2
                  const off = i - mid
                  const rot = off * 7
                  const ty = tuck * (34 + Math.abs(off) * 6)
                  const op = 1 - clamp01((tuck - 0.15) / 0.55)
                  return (
                    <div
                      key={src}
                      className={styles.memory}
                      style={{
                        transform: `translateX(${off * 30}px) translateY(${ty}px) rotate(${rot}deg) scale(${
                          1 - tuck * 0.12
                        })`,
                        opacity: op,
                        zIndex: 10 - Math.abs(off),
                        transition: noTransition ? "none" : undefined,
                        backgroundImage: `url(${src})`,
                      }}
                    />
                  )
                })}
              </div>
              {/* Inner shadow lip so photos read as being INSIDE the pouch */}
              <div className={styles.mouthLip} />
            </div>

            {/* Embossed crest — hidden behind the memories, revealed on seal */}
            <div className={styles.crest} aria-hidden>
              <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden>
                <path
                  d="M12 3l7 2.6v5.2c0 4.7-3 7.9-7 9.2-4-1.3-7-4.5-7-9.2V5.6L12 3z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 8.8V7.7a3 3 0 0 1 6 0v1.1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <rect
                  x="8"
                  y="8.8"
                  width="8"
                  height="6.2"
                  rx="1.7"
                  fill="currentColor"
                />
              </svg>
            </div>

            {/* Sealed face — the four protections, lit together */}
            <div className={styles.face} aria-hidden>
              <div className={styles.seals}>
                {GUARDS.map((g, i) => (
                  <div
                    className={styles.seal}
                    key={g.label}
                    style={{
                      transitionDelay: `${(sealed ? i : GUARDS.length - 1 - i) * 55}ms`,
                    }}
                  >
                    <span className={styles.sealIcon}>{g.icon}</span>
                    <span className={styles.sealText}>
                      <b>{g.label}</b>
                      <i>{g.sub}</i>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Zipper seam (the mouth's closing mechanism) ──────────────── */}
            <div className={styles.seamWrap}>
              <div
                className={styles.track}
                ref={trackRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={settle}
                onPointerCancel={settle}
                role="slider"
                aria-label="Seal your vault"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (
                    e.key === "ArrowRight" ||
                    e.key === "Enter" ||
                    e.key === " "
                  ) {
                    e.preventDefault()
                    setProgress((p) =>
                      e.key === "ArrowRight" ? clamp01(p + 0.2) : 1,
                    )
                  } else if (e.key === "ArrowLeft") {
                    e.preventDefault()
                    setProgress((p) => clamp01(p - 0.2))
                  }
                }}
              >
                {/* Open edges (unfused) sit under everything */}
                <div className={styles.edgesOpen} />
                {/* Fused seam grows from the left as you zip */}
                <div
                  className={styles.seamClosed}
                  style={{
                    width: closedPct,
                    transition: noTransition ? "none" : undefined,
                  }}
                >
                  <div className={styles.stitch} />
                </div>
                {/* The lock-shaped pull you drag */}
                <div
                  className={`${styles.pull} ${!dragging && progress < 0.02 ? styles.pullHint : ""}`}
                  style={{
                    left: pullLeft,
                    transition: noTransition ? "none" : undefined,
                  }}
                >
                  <div className={styles.pullHead}>
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
                      <path
                        d="M8 10V7.4a4 4 0 0 1 8 0V10"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <rect
                        x="5.6"
                        y="10"
                        width="12.8"
                        height="9"
                        rx="2.4"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className={styles.pullTab} />
                </div>
              </div>
              <div className={styles.hint}>
                {sealed
                  ? "Sealed — encrypted & synced"
                  : "Drag to seal your vault"}
              </div>
            </div>
          </div>
        </div>

        {/* ── Plans — yearly is the hero ──────────────────────────────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.bestTag}>Save {savings}%</span>
            <span className={styles.planMain}>
              <span className={styles.radio} data-on={plan === "yearly"} />
              <span className={styles.planCopy}>
                <span className={styles.planName}>12 months</span>
                <span className={styles.planNote}>
                  {yearly?.hasTrial
                    ? "7-day free trial, then billed yearly"
                    : "billed yearly"}
                </span>
              </span>
              <span className={styles.priceCol}>
                <span className={styles.priceRow}>
                  <b className={styles.priceBig}>{yearlyPrice}</b>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.planOn : ""}`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.radio} data-on={plan === "monthly"} />
            <span className={styles.planNameSm}>Monthly</span>
            <span className={styles.priceSm}>
              {monthlyPrice}
              <span className={styles.pricePerSm}>/mo</span>
            </span>
          </button>
        </div>

        {/* ── CTA ─────────────────────────────────────────────────────────── */}
        <button className={styles.cta} onClick={purchase}>
          <span className={styles.ctaLabel}>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden>
            <path
              d="M5 12h13M13 6l6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className={styles.ctaSub}>{ctaSub}</p>
        <p className={styles.reassure}>
          Your memories are always yours — nothing is ever deleted or locked
          away. Premium only adds protection.
        </p>

        {/* ── Legal ───────────────────────────────────────────────────────── */}
        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default TuckPaywall
